import {
    createSlice,
    createAsyncThunk,
    // PayloadAction,
} from '@reduxjs/toolkit'
import { PayloadAction } from '@reduxjs/toolkit/dist/createAction'
import {
    getAnalytics,
    getApp,
    putPricing,
    putAvailability,
    putWorksheets,
} from 'services/appServices'
import {
    getAdmin,
    getData,
    putData,
    postData,
    deleteData,
    postMediaFiles,
    putMedia,
    deleteMedia,
    postStartJob,
    getJob,
    postAppData,
    putAppData,
    deleteAppData,
    postGallery,
    putGallery,
    deleteGallery,
    postMedia,
    postMediaTag,
    putMediaTag,
    deleteMediaTag,
    getMediaThumbnails,
    getMediaExport,
    putMediaAdditional,
    putMap,
    postMap,
    getMap,
    getLogList,
    getLog,
    postRunOperation,
    postContent,
    postLocation,
    deleteLocation,
    deleteContent,
    postMediaThumbnails,
    putOrganizationData,
    postOrganizationData,
    deleteOrganizationData,
    postLLMContent,
    postMediaDownload,
    postLLMContext,
    getLLMContext,
    deleteLLMContext,
    getDocumentationLinks,
    saveDocumentationLink,
    deleteDocumentationLink,
    getAnalyticsDashboard,
    getAnalyticsDashboards,
    postAnalyticsDashboard,
    putAnalyticsDashboard,
    deleteAnalyticsDashboard,
    getTiles,
    getTile,
    postTile,
    putTile,
    deleteTile,
    updateTilePositions,
    getMarketingSpend,
    putMarketingSpend,
    importMarketingSpend,
    getMarketingSpendHistory,
    getMarketingSpendBatch,
    deleteMarketingSpendBatch,
    deleteMarketingSpendEntry,
    updateMarketingSpendEntry,
    getSalesTracking,
    postSalesTracking,
    postSalesTrackingBulk,
    putSalesTracking,
    deleteSalesTracking,
    setAnalyticsDashboardAsDefault,
    searchProjectUsers as searchProjectUsersService,
    postDemoApp,
    postTemplateClone,
    postTemplateCloneToApp,
    postTemplateCloneToOrganization,
    postMediaOptimize,
    getTemplatePages
} from 'services/adminServices'
import {
    AdminDelta,
    AdminView,
    Analytics,
    AdminState,
    ErrorMessage,
    NestedDict,
    RootState,
    PageType,
    MediaTagData,
    UrlOption,
    MediaData,
    SegmentOptions,
    MapData,
    CustomContentData,
    AppState,
    OrganizationData,
    LLMGenerateType,
    AppData,
} from 'app/types'
import { getMediaLink } from 'helpers/media'
import { logger } from 'helpers/logger'
import * as fnc from 'helpers/fnc'
import {
    resolvePending,
    editApp,
    setPath,
    applyTheming,
    addProjectMedia,
    updateProjectMedia,
    deleteProjectMedia,
    updateAppContent,
    updateAppLocation,
    removeAppLocation,
    removeAppContent,
    updateGallery,
    updateProjectCurrent,
    updateOrganizationLocation,
    updatePromptLocation,
    updatePrompt,
} from './appActions'
import {
    updateUser,
} from './userActions'
import {
    retryOperation,
    setupApp,
    setupAppMaps,
    setupAppMeta,
    setupPages,
    setupUpgrades,
} from './actionHelpers'
import { putUser } from 'services/userServices'
import { Action } from '@babylonjs/core/Actions/action'


const debugErrors = process.env.DEBUG == 1
// const cookies = new Cookies()

/**
 * Initial state
 */
const initialState: AdminState = {
    changePending: false,
    initialized: false,
    delta: {},
    error: {},
    customDomain: {},
    lastSave: Date.now(),
    editInline: localStorage.getItem('editInline') == 'true',
    editContent: null,
    llmContext: null,
    documentationLinks: []
}

function saveState(state: AdminState) {
    localStorage.setItem('editInline', state.editInline ? 'true' : 'false')
}

/**
 * Create a default "delta" for admin changes
 */
function setDefaultDelta(state: AdminState) {
    const views = Object.keys(AdminView)
    for (let i = 0; i < views.length; i += 1) {
        const view: string = AdminView[views[i]]
        state.delta[view] = {}
    }
}
setDefaultDelta(initialState)

/**
 * Replace state data at matching id
 */
function removeFromList(id, data) {
    const newData = [...data]
    const index = newData.findIndex((x) => x.id === id)
    if (index !== -1) {
        newData.splice(index, 1)
        return newData
    }
    return data
}

/**
 * Async actions
 */

/**
 * Initialize the administrative page of the application
 */
export const initializeAdmin = createAsyncThunk<AdminState,
    { appLink: string, organization: OrganizationData }>(
        'admin/initializeAdmin',
        async ({ appLink, options }, { rejectWithValue, getState, dispatch }) => {
            let finalOptions = {
                theme: true,
                ...options,
            }
            const tryAdmin = () => {
                return getAdmin(finalOptions)
                    .then((x: AdminState) => {
                        if (!x.apps) {
                            throw new Error(x)
                        }
                        if (finalOptions.theme) {
                            applyTheming(getState, dispatch, null, null)
                        }
                        // if (!getState().app.config.colorScheme) {
                        // if (getState().app.standaloneApp && getState().app.projectCurrent[0] != null) {
                        //     applyTheming(getState, dispatch, null, getState().app.projectCurrent[0].meta)
                        // } else if (x.organization && !getState().admin.organization) {
                        //     applyTheming(getState, dispatch, x.organization)
                        // }
                        // }
                        dispatch(setPath({ appLink }))
                        // Copy config from app
                        return { ...x, config: getState().app.config, media: getState().app.media }
                    })
            }
            return retryOperation(tryAdmin)
                .catch((e) => {
                    applyTheming(getState, dispatch, null, null)
                    // applyTheming(getState, dispatch, options.organization)
                    return rejectWithValue(e)
                })
        },
    )

/**
 * Retrieve single app, for viewing additional information
 */
export const initializeAdminProject = createAsyncThunk<AdminState,
    { appLink: string, organizationLink: string, splitIndex: string }>(
        'admin/initializeAdminProject',
        async ({ appLink, organizationLink, options }, { dispatch, getState, rejectWithValue }) => {
            let finalOptions = {
                theme: true,
                ...options,
            }
            const tryApp = () => getApp(appLink, organizationLink, false, true)
                .then((x) => {
                    if (!x || !x.meta) {
                        logger.error('Error loading app', x)
                        throw new Error('Error loading app')
                    }
                    dispatch(addProjectMedia(x.media))

                    // See if this app is part of any organizations
                    let organization = null
                    if (x.organizations && x.organizations.length) {
                        organization = getState().admin.config.organizations
                            .find((y) => y.id == x.organizations[0])
                    }

                    if (getState().app.standaloneApp || true) {
                        // applyTheming(getState, dispatch, organization, x.meta)
                        return { ...x }
                    }
                    else if (organization) {//((organization && !organization.published)) {
                        // applyTheming(getState, dispatch, organization)
                        return { organization, ...x }
                    }

                    if (finalOptions.theme) {
                        applyTheming(getState, dispatch, null)
                    }
                    return { ...x }
                })
            return retryOperation(tryApp)
                .catch(rejectWithValue)
        },
    )

/**
 * Retrieve data for analytics charts
 */
export const retrieveAnalytics = createAsyncThunk<NestedDict,
    { app: AppData, type: Analytics, csv: boolean, options: Dict }, { state: RootState }>(
        'admin/retrieveAnalytics',
        async ({
            app,
            csv,
            type,
            options,
        }, { rejectWithValue }) => {
            const tryAnalytics = () => getAnalytics(app, type, options)
            return retryOperation(tryAnalytics)
                .catch(rejectWithValue)
        },
    )
/**
 * Retrieve data for management
 */
export const retrieveData = createAsyncThunk<NestedDict,
    { view: AdminView, options: Dict | Dict[] }, { state: RootState }>(
        'admin/retrieveData',
        ({ view, options }, { dispatch, rejectWithValue }) => {
            let finalView = view.singular()
            if (finalView.startsWith('all-')) {
                finalView = finalView.replace('all-', '')
            }
            if (finalView.includes('project')) {
                finalView = finalView.replace('project', 'app')
            }
            const tryData = () => getData(finalView, options)
                .then((x) => {
                    let key = finalView
                    if (key in x) {
                        if (Array.isArray(x[key])) {
                            return { view, data: x[key] }
                        }
                        // Extract media if relevant
                        const {
                            media,
                            ...other
                        } = x[key]
                        if (media) {
                            dispatch(addProjectMedia(media))
                        }

                        return { view, data: other }
                    }
                    throw x
                })
            return retryOperation(tryData)
                .catch(rejectWithValue)
        },
    )

/**
 * Perform an edit to values from the administration panel
 */
export const updateData = createAsyncThunk<AdminDelta,
    { view: AdminView, delta: AdminDelta, app: AppData, options: UrlOption }, { state: RootState }>(
        'admin/updateData',
        async (params, { dispatch, getState, rejectWithValue }) => {
            await new Promise((resolve) => setTimeout(resolve, 500))
            let { view, delta, app, options } = params
            let newDelta = null
            let ret = null

            // Edge case since media editor is a separate thing
            if (view == AdminView.Media) {
                view = 'mediatags'
                app = null
            }
            if (view.startsWith('all-')) {
                view = view.replace('all-', '')
            }
            if (view.includes('project')) {
                view = view.replace('project', 'app')
            }

            let key = view.singular()

            const tryUpdate = async () => {
                switch (view) {
                    case AdminView.Profile:
                        if (delta && Object.keys(delta).length > 0) {
                            await putUser(getState().user.data.id, delta)
                            dispatch(updateUser(delta))
                        }
                        newDelta = delta
                        break
                    case AdminView.Pricing:
                        if (delta && Array.isArray(delta) && delta.length > 0) {
                            await putPricing(app, delta)
                        }
                        newDelta = delta
                        break
                    case AdminView.MarketingSpend:
                        if (delta && Array.isArray(delta) && delta.length > 0) {
                            await putMarketingSpend({ marketingSpend: delta }, { app })
                        }
                        newDelta = delta
                        break
                    case AdminView.Availability:
                        if (delta && Array.isArray(delta) && delta.length > 0) {
                            await putAvailability(app, delta)
                        }
                        newDelta = delta
                        break
                    case AdminView.Worksheets:
                        if (delta && Array.isArray(delta) && delta.length > 0) {
                            await putWorksheets(app, delta)
                        }
                        newDelta = delta
                        break
                    case AdminView.SEOOptimization:
                        if (delta && Array.isArray(delta) && delta.length > 0) {
                            logger.info('SEO updateData: delta type:', typeof delta, 'is array:', Array.isArray(delta), 'delta:', delta)
                            if (delta[0] && typeof delta[0] === 'object' && delta[0]._reactName) {
                                logger.error('ERROR: Delta contains SyntheticBaseEvent!', delta)
                                throw new Error('Delta contains SyntheticBaseEvent - this should not happen')
                            }
                            const { putSEO } = await import('services/appServices')
                            const state = getState()
                            const organization = state.admin.organization
                            await putSEO(app, delta, organization)
                        }
                        newDelta = delta
                        break
                    case AdminView.SalesTracking:
                        if (delta && Object.keys(delta).length > 0) {
                            // Convert delta object to array of sales tracking entries
                            ret = await postSalesTrackingBulk(Object.values(delta), app)
                            newDelta = ret?.data || []
                        }
                        break
                    case AdminView.AllProjects:
                        // Apps should use /admin/app/{appId} not /admin/apps/{appId}
                        if (delta && delta.id != null) {
                            // Use the correct 'app' endpoint, not 'apps'
                            ret = await putData('app', delta)
                        } else {
                            // Use the correct 'app' endpoint for creation too
                            ret = await postData('app', delta, options)
                        }

                        if (ret && ret.failures && ret.failures.length > 0) {
                            if (debugErrors) {
                                throw new Error({ success: 0, message: ret.failures.map(JSON.stringify).join(','), status: 422, data: ret.failures })
                            } else {
                                throw new Error({ success: 0, message: ErrorMessage.UpdateError, status: 422 })
                            }
                        }
                        if (!ret || !('success' in ret)) {
                            throw new Error({ success: 0, message: ErrorMessage.UpdateError, status: 422 })
                        }
                        const { success: appsSuccess, message: appsMessage, status: appsStatus, ...appsOther } = ret
                        if (Object.keys(appsOther).length == 1 && view != AdminView.General) {
                            newDelta = Object.values(appsOther)[0]
                        } else {
                            newDelta = appsOther
                        }
                        break
                    default:
                        ret = null
                        if (!delta || delta.id == null) {
                            if (app) {
                                ret = await postAppData(app, key, delta, options)
                            } else {
                                ret = await postData(key, delta, options)
                            }
                        } else if (app) {
                            ret = await putAppData(app, key, delta, options)
                        } else {
                            ret = await putData(key, delta, options)
                        }

                        if (ret && ret.failures && ret.failures.length > 0) {
                            if (debugErrors) {
                                throw new Error({ success: 0, message: ret.failures.map(JSON.stringify).join(','), status: 422, data: ret.failures })
                            } else {
                                throw new Error({ success: 0, message: ErrorMessage.UpdateError, status: 422 })
                            }
                        }
                        if (!ret || !('success' in ret)) {
                            throw new Error({ success: 0, message: ErrorMessage.UpdateError, status: 422 })
                        }
                        const { success, message, status, ...other } = ret
                        if (Object.keys(other).length == 1 && view != AdminView.General) {
                            newDelta = Object.values(other)[0]
                        } else {
                            newDelta = other
                        }
                        break
                }

                dispatch(resolvePending(true))
                return { view, delta: newDelta }
            }

            return retryOperation(tryUpdate)
                .catch((e) => {
                    dispatch(resolvePending(e))
                    return rejectWithValue({ view, error: e })
                })
        },
    )

export const removeData = createAsyncThunk<NestedDict,
    { app: AppData, view: AdminView, id: string }, { state: RootState }>(
        'admin/removeData',
        async ({ app, view, id }, { dispatch, rejectWithValue }) => {
            const tryRemove = () => {
                let prom = null
                let key = view.singular()
                if (app) {
                    prom = deleteAppData(app, key, id)
                } else {
                    prom = deleteData(key, id)
                }
                return prom.then((x) => {
                    dispatch(resolvePending(true))
                    if (x.id) {
                        return { view, id: x.id }
                    }
                    return { view, id: null }
                })
            }
            return retryOperation(tryRemove)
                .catch((x) => {
                    dispatch(resolvePending(false))
                    return rejectWithValue(x)
                })
        },
    )

/**
 * Update Project
 */
export const updateProject = createAsyncThunk<AdminDelta,
    { view: AdminView, data: AppData }, { state: RootState }>(
        'admin/updateProject',
        ({ view, data }, { dispatch, rejectWithValue }) => {
            const tryUpdate = async () => {
                const ret = await putData(AdminView.AllProjects, data)
                const newDelta = ret[AdminView.AllProjects]
                dispatch(resolvePending(true))
                return { view, data: newDelta, success: true }
            }
            return retryOperation(tryUpdate)
                .catch((e) => {
                    dispatch(resolvePending(e))
                    return rejectWithValue({ view: view, error: e })
                })
        },
    )

/**
 * Add page
 */
export const upsertPage = createAsyncThunk<Dict,
    { app: AppData, organization: OrganizationData, page: PageData }, { state: RootState }>(
        'admin/upsertPage',
        ({ app, organization, page }, { getState, dispatch, rejectWithValue }) => {
            const tryAddPage = () => {
                let pageArr = Array.isArray(page) ? page : [page]
                let prom = Promise.all(pageArr.map((page) => {
                    const newPage = {
                        ...page,
                    }
                    if (page.pageType) {
                        const pageTypeData = Object.values(getState().app.config.pageTypes).find((x) => x.name == page.pageType)
                        if (!pageTypeData) {
                            throw new Error(`Missing config for ${page.pageType}`)
                        }
                        if (!newPage.name) {
                            newPage.name = page.pageType.toTitleCase()
                        }
                        if (!newPage.link) {
                            newPage.link = page.pageType
                        }
                        if (page.pageTypeId == null) {
                            newPage.pageTypeId = pageTypeData.id
                        }
                    }
                    let prom = null
                    if (app) {
                        newPage.appId = app.meta.id
                        if (newPage.id) {
                            return putAppData(app, 'page', newPage)
                        } else {
                            return postAppData(app, 'page', newPage)
                        }
                    } else if (organization) {
                        newPage.organizationId = organization.id
                        if (newPage.id) {
                            return putOrganizationData(organization, 'page', newPage)
                        } else {
                            return postOrganizationData(organization, 'page', newPage)
                        }
                    }
                }))
                if (!prom) {
                    throw new Error('No pages to add')
                }
                return prom.then((x) => {
                    let ret = Array.isArray(x) ? x[0] : x
                    if (ret && ret.success) {
                        dispatch(resolvePending(true))
                    } else {
                        dispatch(resolvePending(ret))
                    }
                    return x
                })
            }
            return retryOperation(tryAddPage)
                .catch((x) => {
                    dispatch(resolvePending(x))
                    return rejectWithValue(x)
                })
        },
    )

export const removePage = createAsyncThunk<Dict,
    { app: AppData, organizaiton: OrganizationData, page: PageData }, { state: RootState }>(
        'admin/removePage',
        ({ app, organization, page }, { getState, dispatch, rejectWithValue }) => {
            if (!app && organization) {
                return deleteOrganizationData(organization, 'page', page.id)
            } else {
                return deleteAppData(app, 'page', page.id)
            }
        })

/**
 * Media
 */
export const addMediaFiles = createAsyncThunk<Dict,
    { files: Array, onUploadProgress: () => void, options: SegmentOptions, metadata?: Dict }, { state: RootState }>(
        'admin/addMediaFiles',
        ({ files, onUploadProgress, options, metadata }, { dispatch, rejectWithValue }) => {
            const tryPostMediaFiles = () => {
                return postMediaFiles(files, onUploadProgress, options, metadata)
            }
            return retryOperation(tryPostMediaFiles)
                .catch(rejectWithValue)
        },
    )

export const addMediaFromUrl = createAsyncThunk<Dict,
    { media: MediaData, options: SegmentOptions }, { state: RootState }>(
        'admin/addMediaUrl',

        ({ media, options }, { dispatch, rejectWithValue }) => {
            const tryAddMedia = () => {
                return postMediaDownload(media, options)
                    .then((x) => {
                        if (x.success && x.data) {
                            dispatch(addProjectMedia([x.data]))
                        }
                        return x
                    })
            }
            return retryOperation(tryAddMedia)
                .catch(rejectWithValue)
        },
    )

export const addMedia = createAsyncThunk<Dict,
    { media: MediaData, options: SegmentOptions }, { state: RootState }>(
        'admin/addMedia',
        ({ media, options }, { dispatch, rejectWithValue }) => {
            const tryAddMedia = () => {
                return postMedia(media, options)
            }
            return retryOperation(tryAddMedia)
                .catch(rejectWithValue)
        },
    )


export const updateMedia = createAsyncThunk<Dict,
    { data: MediaData, options: SegmentOptions }, { state: RootState }>(
        'admin/updateMedia',
        ({ data, options }, { dispatch, rejectWithValue }) => {
            const tryPutMedia = () => {
                return putMedia(data, options)
                    .then((x) => {
                        dispatch(updateProjectMedia(data))
                    })
            }
            return retryOperation(tryPutMedia)
                .catch(rejectWithValue)
        },
    )

export const removeMedia = createAsyncThunk<Dict,
    { media: string[], options: SegmentOptions }, { state: RootState }>(
        'admin/removeMedia',
        ({ media, options }, { dispatch, rejectWithValue }) => {
            const tryRemoveMedia = () => {
                return deleteMedia(media, options)
                    .then(() => {
                        dispatch(deleteProjectMedia(media))
                    })
            }
            return retryOperation(tryRemoveMedia)
                .catch(rejectWithValue)
        },
    )


export const generateMediaThumbnails = createAsyncThunk<Dict,
    { media: MediaData, options: SegmentOptions }, { state: RootState }>(
        'admin/generateMediaThumbnails',
        ({ media, options, file }, { dispatch, rejectWithValue }) => {
            const tryGenMedia = () => {
                if (file) {
                    return postMediaThumbnails(media, options, file)
                } else {
                    return getMediaThumbnails(media, options, file)
                }
            }
            return retryOperation(tryGenMedia)
                .catch(rejectWithValue)
        },
    )

export const exportMediaFile = createAsyncThunk<Dict,
    { media: MediaData, options: SegmentOptions }, { state: RootState }>(
        'admin/exportMediaFile',
        ({ media, options }, { dispatch, rejectWithValue }) => {
            const tryExportFile = () => {
                return getMediaExport(media, options)
            }
            return retryOperation(tryExportFile)
                .catch(rejectWithValue)
        },
    )

export const optimizeMediaFile = createAsyncThunk<Dict,
    { media: MediaData, options: SegmentOptions }, { state: RootState }>(
        'admin/optimizeMediaFile',
        ({ media, options }, { dispatch, rejectWithValue }) => {
            const tryOptimizeFile = () => {
                return postMediaOptimize(media, options)
                    .then((result) => {
                        // If the optimization returned updated media data, dispatch updateProjectMedia
                        // to ensure the Redux state reflects the optimized values
                        if (result && result.media) {
                            dispatch(updateProjectMedia(result.media))
                        }
                        return result
                    })
            }
            return retryOperation(tryOptimizeFile)
                .catch(rejectWithValue)
        },
    )

export const upsertMap = createAsyncThunk<Dict,
    MapData, { state: RootState }>(
        'admin/upsertMap',
        (map, { dispatch, rejectWithValue }) => {
            const tryUpdateMap = () => {
                if (map.id) {
                    return putMap(map)
                } else {
                    return postMap(map)
                }
            }
            return retryOperation(tryUpdateMap)
                .catch(rejectWithValue)
        },
    )


export const retrieveMap = createAsyncThunk<Dict,
    { link: string, options: SegmentOptions }, { state: RootState }>(
        'admin/retrieveMap',
        ({ link, options }, { getState, dispatch, rejectWithValue }) => {
            const tryRetrieveMap = () => {
                return getMap(link, options)
            }

            return retryOperation(tryRetrieveMap)
                .catch((x) => {
                    return rejectWithValue(x)
                })
        },
    )

export const pollJob = createAsyncThunk<Dict,
    { app: AppData, pageType: PageType }, { state: RootState }>(
        'admin/pollJob',
        (jobId, { rejectWithValue }) => {
            const tryPollJob = () => getJob(jobId)
            return retryOperation(tryPollJob)
                .catch(rejectWithValue)
        },
    )

export const startJob = createAsyncThunk<Dict,
    { app: AppData, pageType: PageType }, { state: RootState }>(
        'admin/startJob',
        (jobId, { rejectWithValue }) => {
            const tryStartJob = () => {
                return postStartJob(jobId)
                    .then(() => {
                        return true
                    })
            }
            return retryOperation(tryStartJob)
                .catch(rejectWithValue)
        },
    )

export const addMediaTag = createAsyncThunk<Dict,
    { data: MediaTagData, options: SegmentOptions }, { state: RootState }>(
        'admin/addMediaTag',
        ({ data, options }, { rejectWithValue }) => {
            const tryAddMediaTag = () => {
                if (data.id) {
                    return putMediaTag(data, options)
                } else {
                    return postMediaTag(data, options)
                }
            }
            return retryOperation(tryAddMediaTag)
                .catch(rejectWithValue)
        },
    )

export const removeMediaTag = createAsyncThunk<Dict,
    { data: MediaTagData, options: SegmentOptions }, { state: RootState }>(
        'admin/removeMediaTag',
        ({ data, options }, { rejectWithValue }) => {
            const tryRemoveMediaTag = () => deleteMediaTag(data, options)
            return retryOperation(tryRemoveMediaTag)
                .catch(rejectWithValue)
        },
    )


/**
 * Galleries
 */
export const upsertGallery = createAsyncThunk<Dict,
    { data: MediaData, options: SegmentOptions }, { state: RootState }>(
        'admin/upsertGallery',
        ({ data, options }, { dispatch, rejectWithValue, getState }) => {
            const tryUpsertGallery = () => {
                let prom = null
                if (data.id) {
                    prom = putGallery(data, options)
                } else {
                    prom = postGallery(data, options)
                }
                return prom.then((x) => {
                    // If is current project
                    // dispatch(updateProject(x))
                    // if (getState().project?.id === getState().app.projectCurrent[0]?.id) {
                    // dispatch(updateGallery(x))
                    // }
                    // if (options.organization) {
                    //     const newGalleries = options.organizations.galleries
                    //     const idx = newGalleries.findIndex((x) => x.id === data.id)
                    //     if (idx !== -1) {
                    //         newGalleries[idx] = x
                    //     } else {
                    //         newGalleries.push(x)
                    //     }
                    //     dispatch(updatePrompt({ organization: { ...options.organization, galleries: newGalleries } }))
                    // }
                    return x
                })
            }
            return retryOperation(tryUpsertGallery)
                .catch(rejectWithValue)
        },
    )

export const removeGallery = createAsyncThunk<Dict,
    { data: GalleryData, options: SegmentOptions }, { state: RootState }>(
        'admin/deleteGallery',
        ({ data, options }, { rejectWithValue }) => {
            const tryDeleteGallery = () => deleteGallery(data, options)
            return retryOperation(tryDeleteGallery)
                .catch(rejectWithValue)
        },
    )

/**
 * Admin functions
 */
export const retrieveLogList = createAsyncThunk<Dict, LogType, { state: RootState }>(
    'admin/retrieveLogList',
    (logType, { rejectWithValue }) => {
        const tryRetrieveLogs = () => getLogList(logType)
        return retryOperation(tryRetrieveLogs)
            .catch(rejectWithValue)
    },
)
export const retrieveLog = createAsyncThunk<Dict, { type: LogType, filename: string }, { state: RootState }>(
    'admin/retrieveLog',
    ({ logType, filename }, { rejectWithValue }) => {
        const tryRetrieveLog = () => getLog(logType, filename)
        return retryOperation(tryRetrieveLog)
            .catch(rejectWithValue)
    },
)

export const runOperation = createAsyncThunk<Dict, { operation: AdminOperation, data: Dict }, { state: RootState }>(
    'admin/runOperation',
    ({ operation, data }, { dispatch, rejectWithValue }) => {
        const tryRunOperation = () => postRunOperation(operation, data)
        return retryOperation(tryRunOperation)
            .then((x) => {
                dispatch(resolvePending(x))
            }).catch(() => {
                dispatch(resolvePending(x))
                rejectWithValue(x)
            })
    },
)

/**
 * Content
 */
export const upsertContent = createAsyncThunk<Dict,
    { app: AppData, organization: OrganizationData, data: CustomContentData }, { state: RootState }>('admin/upsertContent',
        ({ app, organization, data }, { dispatch, rejectWithValue }) => {
            const tryCreate = async () => {
                return postContent(app, organization, data)
                    .then((x) => {
                        if (x.data) {
                            // dispatch(updateAppContent(x.data))
                            // dispatch(updateEditContent(x.data))
                            return x.data
                        } else {
                            return rejectWithValue(x)
                        }

                    })
            }
            return retryOperation(tryCreate)
                .catch((e) => {
                    dispatch(resolvePending(e))
                    return rejectWithValue({ error: e })
                })
        },
    )

export const batchUpsertContent = createAsyncThunk<Dict[],
    { app: AppData, organization: OrganizationData, items: CustomContentData[] }, { state: RootState }>('admin/batchUpsertContent',
        async ({ app, organization, items }, { dispatch, rejectWithValue }) => {
            try {
                const results = await Promise.all(
                    items.map((data) =>
                        retryOperation(() =>
                            postContent(app, organization, data).then((x) => {
                                if (x.data) return x.data
                                throw new Error('No data returned')
                            })
                        )
                    )
                )
                logger.info('[Reorder] batchUpsertContent - All items saved:', results.length)
                return results
            } catch (e) {
                dispatch(resolvePending(e))
                return rejectWithValue({ error: e })
            }
        },
    )

export const removeContent = createAsyncThunk<Dict,
    { app: AppData, organization: OrganizationData, data: CustomContentData }, { state: RootState }>(
        'admin/removeContent',
        ({ app, organization, data }, { dispatch, rejectWithValue }) => {
            const tryRemove = async () => {
                return deleteContent(app, organization, data)
                    .then((x) => {
                        if (x.success) {
                            // dispatch(removeAppContent(data))
                            return x.data
                        } else {
                            return rejectWithValue(x)
                        }
                    })
            }
            return retryOperation(tryRemove)
                .catch((e) => {
                    dispatch(resolvePending(e))
                    return rejectWithValue({ error: e })
                })
        },
    )

export const createLLMContent = createAsyncThunk<Dict,
    { app: AppData, organization: OrganizationData, data: CustomContentData }, { state: RootState }>(
        'admin/createLLMContent',
        ({ data, app, organization }, { dispatch, rejectWithValue }) => {
            const tryCreate = async () => {
                return postLLMContent(data, app, organization)
                    .then((x) => {
                        if (x.data) {
                            dispatch(resolvePending(x))
                            return x.data
                        } else {
                            dispatch(resolvePending({ success: false, failureMessage: 'Content generation failed' }))
                            return rejectWithValue(x)
                        }
                    })
            }
            return retryOperation(tryCreate)
                .catch((e) => {
                    dispatch(resolvePending(e))
                    return rejectWithValue({ error: e })
                })
        },
    )

/**
 * Context
 */
export const retrieveLLMContext = createAsyncThunk<Dict,
    { app: AppData, organization: OrganizationData }, { state: RootState }>(
        'admin/content/retrieveLLMContext',
        ({ app, organization }, { dispatch, rejectWithValue }) => {
            const tryRetrieveContext = async () => {
                return getLLMContext(app, organization)
                    .then((x) => {
                        if (x.data) {
                            return x.data
                        } else {
                            return rejectWithValue({ error: 'Failed to retrieve context', data: x })
                        }
                    })
            }
            return retryOperation(tryRetrieveContext)
                .catch((e) => {
                    dispatch(resolvePending(e)) // Ensure pending state is resolved on error
                    return rejectWithValue({ error: e })
                })
        }
    )

export const createLLMContext = createAsyncThunk<Dict,
    { app: AppData, organization: OrganizationData, data: CustomContentData }, { state: RootState }>(
        'admin/content/createLLMContext',
        ({ app, organization, data }, { dispatch, rejectWithValue }) => {
            const tryCreateContext = async () => {
                return postLLMContext(data, app, organization)
                    .then((x) => {
                        if (x.data) {
                            dispatch(resolvePending(x.data))
                            return x.data
                        } else {
                            dispatch.resolvePending({ success: false, failureMessage: 'Context creation failed' })
                            return rejectWithValue(x)
                        }
                    })
            }
            return retryOperation(tryCreateContext)
                .catch((e) => {
                    dispatch(resolvePending(e))
                    return rejectWithValue({ error: e })
                })
        },
    )

export const removeLLMContext = createAsyncThunk<Dict,
    { app: AppData, organization: OrganizationData, data: CustomContentData }, { state: RootState }>(
        'admin/content/removeLLMContext',
        ({ app, organization, data }, { dispatch, rejectWithValue }) => {
            const tryRemoveContext = async () => {
                return deleteLLMContext(data, app, organization)
                    .then((x) => {
                        if (x.data.success) {
                            dispatch(resolvePending(x.data))
                            return x.data
                        } else {
                            dispatch.resolvePending({ success: false, failureMessage: 'Context removal failed' })
                            return rejectWithValue(x.data)
                        }
                    })
            }
            return retryOperation(tryRemoveContext)
                .catch((e) => {
                    dispatch(resolvePending(e))
                    return rejectWithValue({ error: e })
                })
        },
    )

/**
 * Location
 */
export const createLocation = createAsyncThunk<Dict,
    { app: AppData, organization: OrganizationData, data: CustomContentData }, { state: RootState }>(
        'admin/createLocation',
        ({ app, organization, data }, { dispatch, rejectWithValue }) => {
            const tryCreate = async () => {
                return postLocation(app, organization, data)
                    .then((x) => {
                        if (x.data) {
                            if (app) {
                                dispatch(updateAppLocation(x.data))
                            } else {
                                dispatch(updateOrganizationLocation(x.data))
                            }
                            dispatch(updatePromptLocation(x.data))
                            return x.data
                        } else {
                            return rejectWithValue(x)
                        }

                    })
            }
            return retryOperation(tryCreate)
                .catch((e) => {
                    dispatch(resolvePending(e))
                    return rejectWithValue({ error: e })
                })
        },
    )

export const removeLocation = createAsyncThunk<Dict,
    { app: AppData, organization: OrganizationData, data: CustomContentData }, { state: RootState }>(
        'admin/removeLocation',
        ({ app, organization, data }, { dispatch, rejectWithValue }) => {
            const tryRemove = async () => {
                return deleteLocation(app, organization, data)
                    .then((x) => {
                        if (x.success) {
                            dispatch(removeAppLocation(data))
                            return x.data
                        } else {
                            return rejectWithValue(x)
                        }

                    })
            }
            return retryOperation(tryRemove)
                .catch((e) => {
                    dispatch(resolvePending(e))
                    return rejectWithValue({ error: e })
                })
        }
    )

/**
 * Documentation Links
 */
export const fetchDocumentationLinks = createAsyncThunk<Dict, void, { state: RootState }>(
    'admin/fetchDocumentationLinks',
    async (_, { dispatch, rejectWithValue }) => {
        try {
            return await getDocumentationLinks()
        } catch (error) {
            dispatch(resolvePending(error))
            return rejectWithValue({ error })
        }
    }
)

export const fetchDocumentationLinkById = createAsyncThunk<Dict, string, { state: RootState }>(
    'admin/fetchDocumentationLinkById',
    async (id, { dispatch, rejectWithValue }) => {
        try {
            return await getDocumentationLinkById(id)
        } catch (error) {
            return rejectWithValue({ error })
        }
    }
)

export const upsertDocumentationLink = createAsyncThunk<Dict, any, { state: RootState }>(
    'admin/upsertDocumentationLink',
    async (data, { dispatch, rejectWithValue }) => {
        try {
            const result = await saveDocumentationLink(data)
            return result
        } catch (error) {
            return rejectWithValue({ error })
        }
    }
)

export const removeDocumentationLink = createAsyncThunk<Dict, string, { state: RootState }>(
    'admin/removeDocumentationLink',
    async (id, { dispatch, rejectWithValue }) => {
        try {
            const result = await deleteDocumentationLink(id)
            return result
        } catch (error) {
            return rejectWithValue({ error })
        }
    }
)

/**
 * Analytics Dashboards
 */
export const fetchAnalyticsDashboards = createAsyncThunk<Dict,
    { appId?: string, organizationId?: string }, { state: RootState }>(
        'admin/fetchAnalyticsDashboards',
        async ({ appId, organizationId }, { dispatch, rejectWithValue }) => {
            try {
                return await getAnalyticsDashboards(appId, organizationId)
            } catch (error) {
                dispatch(resolvePending(error))
                return rejectWithValue({ error })
            }
        }
    )

export const fetchAnalyticsDashboard = createAsyncThunk<Dict,
    string, { state: RootState }>(
        'admin/fetchAnalyticsDashboard',
        async (dashboardId, { dispatch, rejectWithValue }) => {
            try {
                return await getAnalyticsDashboard(dashboardId)
            } catch (error) {
                return rejectWithValue({ error })
            }
        }
    )

export const createAnalyticsDashboard = createAsyncThunk<Dict,
    any, { state: RootState }>(
        'admin/createAnalyticsDashboard',
        async (dashboardData, { dispatch, rejectWithValue }) => {
            try {
                const result = await postAnalyticsDashboard(dashboardData)
                dispatch(resolvePending(true))
                return result
            } catch (error) {
                dispatch(resolvePending(error))
                return rejectWithValue({ error })
            }
        }
    )

export const updateAnalyticsDashboard = createAsyncThunk<Dict,
    { dashboardId: string, data: any }, { state: RootState }>(
        'admin/updateAnalyticsDashboard',
        async ({ dashboardId, data }, { dispatch, rejectWithValue }) => {
            try {
                const result = await putAnalyticsDashboard(dashboardId, data)
                dispatch(resolvePending(true))
                return result
            } catch (error) {
                dispatch(resolvePending(error))
                return rejectWithValue({ error })
            }
        }
    )

export const removeAnalyticsDashboard = createAsyncThunk<Dict,
    string, { state: RootState }>(
        'admin/deleteAnalyticsDashboard',
        async (dashboardId, { dispatch, rejectWithValue }) => {
            try {
                const result = await deleteAnalyticsDashboard(dashboardId)
                dispatch(resolvePending(true))
                return result
            } catch (error) {
                dispatch(resolvePending(error))
                return rejectWithValue({ error })
            }
        }
    )

export const setAnalyticsDashboardDefault = createAsyncThunk<Dict,
    { dashboardId: string, appId?: string, organizationId?: string }, { state: RootState }>(
        'admin/setAnalyticsDashboardDefault',
        async ({ dashboardId, appId, organizationId }, { dispatch, rejectWithValue }) => {
            try {
                const result = await setAnalyticsDashboardAsDefault(dashboardId, appId, organizationId)
                dispatch(resolvePending(true))
                return result
            } catch (error) {
                dispatch(resolvePending(error))
                return rejectWithValue({ error })
            }
        }
    )

/**
 * Analytics Dashboard Tiles
 */
export const fetchAnalyticsTiles = createAsyncThunk<Dict,
    { dashboardId: string }, { state: RootState }>(
        'admin/fetchAnalyticsTiles',
        async ({ dashboardId }, { rejectWithValue }) => {
            try {
                // getTiles already handles config parsing from string to object
                return await getTiles(dashboardId)
            } catch (error) {
                return rejectWithValue({ error })
            }
        }
    )

export const fetchAnalyticsTile = createAsyncThunk<Dict,
    string, { state: RootState }>(
        'admin/fetchAnalyticsTile',
        async (tileId, { rejectWithValue }) => {
            try {
                // getTile already handles config parsing from string to object
                return await getTile(tileId)
            } catch (error) {
                return rejectWithValue({ error })
            }
        }
    )

export const createAnalyticsTile = createAsyncThunk<Dict,
    any, { state: RootState }>(
        'admin/createAnalyticsTile',
        async (tileData, { dispatch, rejectWithValue }) => {
            try {
                // postTile handles config serialization from object to string
                const result = await postTile(tileData)
                dispatch(resolvePending(true))
                return result
            } catch (error) {
                dispatch(resolvePending(error))
                return rejectWithValue({ error })
            }
        }
    )

export const updateAnalyticsTile = createAsyncThunk<Dict,
    { tileId: string, data: any }, { state: RootState }>(
        'admin/updateAnalyticsTile',
        async ({ tileId, data }, { dispatch, rejectWithValue }) => {
            try {
                // putTile handles config serialization from object to string
                const result = await putTile(tileId, data)
                dispatch(resolvePending(true))
                return result
            } catch (error) {
                dispatch(resolvePending(error))
                return rejectWithValue({ error })
            }
        }
    )

export const removeAnalyticsTile = createAsyncThunk<Dict,
    { tileId: string }, { state: RootState }>(
        'admin/removeAnalyticsTile',
        async ({ tileId }, { dispatch, rejectWithValue }) => {
            try {
                const result = await deleteTile(tileId)
                dispatch(resolvePending(true))
                return result
            } catch (error) {
                dispatch(resolvePending(error))
                return rejectWithValue({ error })
            }
        }
    )

export const updateAnalyticsTilePositions = createAsyncThunk<Dict,
    any[], { state: RootState }>(
        'admin/updateAnalyticsTilePositions',
        async (tiles, { dispatch, rejectWithValue }) => {
            try {
                const result = await updateTilePositions(tiles)
                dispatch(resolvePending(true))
                return result
            } catch (error) {
                dispatch(resolvePending(error))
                return rejectWithValue({ error })
            }
        }
    )

/**
 * Marketing spend
 */

export const retrieveMarketingSpend = createAsyncThunk<Dict,
    { app: AppData, organization: OrganizationData, year: number }, { state: RootState }>(
        'admin/retrieveMarketingSpend',
        async ({ app, organization, year }, { dispatch, rejectWithValue }) => {
            try {
                const result = await getMarketingSpend({ app, organization }, year)
                return result
            } catch (error) {
                dispatch(resolvePending(error))
                return rejectWithValue({ error })
            }
        }
    )

export const upsertMarketingSpend = createAsyncThunk<Dict,
    { app: AppData, organization: OrganizationData, data: any }, { state: RootState }>(
        'admin/upsertMarketingSpend',
        async ({ app, organization, data }, { dispatch, rejectWithValue }) => {
            try {
                const result = await putMarketingSpend(data, { app, organization })
                dispatch(resolvePending(true))
                return result
            } catch (error) {
                dispatch(resolvePending(error))
                return rejectWithValue({ error })
            }
        }
    )

export const importMarketingSpendCsv = createAsyncThunk<Dict,
    { app: AppData, rows: Dict[], importLabel?: string }, { state: RootState }>(
        'admin/importMarketingSpendCsv',
        async ({ app, rows, importLabel }, { dispatch, rejectWithValue }) => {
            try {
                const result = await importMarketingSpend(rows, app, importLabel)
                dispatch(resolvePending(true))
                return result
            } catch (error) {
                dispatch(resolvePending(error))
                return rejectWithValue({ error })
            }
        }
    )

export const retrieveMarketingSpendHistory = createAsyncThunk<Dict,
    { app: AppData }, { state: RootState }>(
        'admin/retrieveMarketingSpendHistory',
        async ({ app }, { dispatch, rejectWithValue }) => {
            try {
                const result = await getMarketingSpendHistory(app)
                return result
            } catch (error) {
                dispatch(resolvePending(error))
                return rejectWithValue({ error })
            }
        }
    )

export const retrieveMarketingSpendBatch = createAsyncThunk<Dict,
    { app: AppData, batchId: string }, { state: RootState }>(
        'admin/retrieveMarketingSpendBatch',
        async ({ app, batchId }, { dispatch, rejectWithValue }) => {
            try {
                const result = await getMarketingSpendBatch(app, batchId)
                return result
            } catch (error) {
                dispatch(resolvePending(error))
                return rejectWithValue({ error })
            }
        }
    )

export const revertMarketingSpendBatch = createAsyncThunk<Dict,
    { app: AppData, batchId: string }, { state: RootState }>(
        'admin/revertMarketingSpendBatch',
        async ({ app, batchId }, { dispatch, rejectWithValue }) => {
            try {
                const result = await deleteMarketingSpendBatch(app, batchId)
                dispatch(resolvePending(true))
                return { batchId, ...result }
            } catch (error) {
                dispatch(resolvePending(error))
                return rejectWithValue({ error })
            }
        }
    )

export const removeMarketingSpendEntry = createAsyncThunk<Dict,
    { app: AppData, entryId: string }, { state: RootState }>(
        'admin/removeMarketingSpendEntry',
        async ({ app, entryId }, { dispatch, rejectWithValue }) => {
            try {
                const result = await deleteMarketingSpendEntry(app, entryId)
                dispatch(resolvePending(true))
                return { entryId, ...result }
            } catch (error) {
                dispatch(resolvePending(error))
                return rejectWithValue({ error })
            }
        }
    )

export const editMarketingSpendEntry = createAsyncThunk<Dict,
    { app: AppData, entryId: string, amount: number, impressions?: number | null }, { state: RootState }>(
        'admin/editMarketingSpendEntry',
        async ({ app, entryId, amount, impressions }, { dispatch, rejectWithValue }) => {
            try {
                const result = await updateMarketingSpendEntry(app, entryId, amount, impressions)
                dispatch(resolvePending(true))
                return result
            } catch (error) {
                dispatch(resolvePending(error))
                return rejectWithValue({ error })
            }
        }
    )

/**
 * Sales Tracking
 */
export const retrieveSalesTracking = createAsyncThunk<Dict,
    { app: AppData }, { state: RootState }>(
        'admin/retrieveSalesTracking',
        async ({ app }, { dispatch, rejectWithValue }) => {
            try {
                const result = await getSalesTracking(app)
                return result
            } catch (error) {
                dispatch(resolvePending(error))
                return rejectWithValue({ error })
            }
        }
    )

export const createSalesTrackingBulk = createAsyncThunk<Dict,
    { app: AppData, salesTrackingArray: any[] }, { state: RootState }>(
        'admin/createSalesTrackingBulk',
        async ({ app, salesTrackingArray }, { dispatch, rejectWithValue }) => {
            try {
                const result = await postSalesTrackingBulk(salesTrackingArray, app)
                dispatch(resolvePending(true))
                return result
            } catch (error) {
                dispatch(resolvePending(error))
                return rejectWithValue({ error })
            }
        }
    )

/**
 * Search Project Users
 */
export const searchProjectUsers = createAsyncThunk<Dict,
    { app: AppData, searchQuery: string }, { state: RootState }>(
        'admin/searchProjectUsers',
        async ({ app, searchQuery }, { dispatch, rejectWithValue, getState }) => {
            try {
                // Get app from admin.project if not provided or invalid
                let validApp = app
                if (!validApp || (!validApp?.meta?.id && !validApp?.id)) {
                    const adminProject = getState()?.admin?.project
                    if (adminProject && (adminProject?.meta?.id || adminProject?.id)) {
                        validApp = adminProject
                    } else {
                        // Serialize the error to make it Redux-compatible
                        return rejectWithValue({
                            error: {
                                message: 'No valid app found for user search',
                                name: 'ValidationError'
                            }
                        })
                    }
                }

                const result = await searchProjectUsersService(searchQuery, validApp)
                return result
            } catch (error) {
                // Serialize the error to make it Redux-compatible
                const serializedError = {
                    message: error?.message || 'Unknown error occurred',
                    name: error?.name || 'Error',
                    stack: error?.stack
                }
                return rejectWithValue({ error: serializedError })
            }
        }
    )

/**
 * Create Demo App
 * Creates a new app based on demo_template and returns the full app data
 */
export const createDemoApp = createAsyncThunk<Dict,
    { link: string }, { state: RootState }>(
        'admin/createDemoApp',
        async ({ link }, { dispatch, rejectWithValue }) => {
            try {
                const result = await postDemoApp(link)
                dispatch(resolvePending(true))
                return result
            } catch (error) {
                dispatch(resolvePending(error))
                return rejectWithValue({ error })
            }
        }
    )

/**
 * Clone Template to Context
 * Generic template cloning that works with both apps and organizations
 */
export const cloneTemplateToContext = createAsyncThunk<Dict,
    {
        pageId: string
        targetContext: { type: 'app' | 'organization', id: string }
        usePlaceholders?: boolean
    }, { state: RootState }>(
        'admin/cloneTemplateToContext',
        async ({ pageId, targetContext, usePlaceholders }, { dispatch, rejectWithValue }) => {
            try {
                let result
                if (targetContext.type === 'app') {
                    result = await postTemplateCloneToApp(pageId, targetContext.id, usePlaceholders)
                } else {
                    result = await postTemplateCloneToOrganization(pageId, targetContext.id, usePlaceholders)
                }

                dispatch(resolvePending(true))
                return result
            } catch (error) {
                dispatch(resolvePending(error))
                return rejectWithValue({ error })
            }
        }
    )

/**
 * Fetch Template Pages
 * Fetches all pages marked as templates (site-wide, not organization-filtered)
 */
export const fetchTemplatePages = createAsyncThunk<Dict, Dict, { state: RootState }>(
    'admin/fetchTemplatePages',
    async (params, { dispatch, rejectWithValue }) => {
        try {
            const result = await getTemplatePages()
            dispatch(resolvePending(true))
            return result
        } catch (error) {
            dispatch(resolvePending(error))
            return rejectWithValue({ error })
        }
    }
)

/**
 * Slice +  sync actions/reducers
 */
const adminSlice = createSlice({
    name: 'admin',
    initialState,
    reducers: {
        // Whether a change is pending in the admin panel
        setChangePending(state: AdminState, action: PayloadAction<boolean>) {
            state.changePending = action.payload
        },
        // Set a pending delta change in admin panel
        setDelta(state: AdminState, action: PayloadAction<AdminDelta>) {
            state.delta = action.payload
        },
        // Reset admin delta
        resetDelta(state: AdminState) {
            setDefaultDelta(state)
            state.changePending = false
        },
        resetAdminError(state: AdminState, action: PayloadAction<AdminView>) {
            if (state.error && action.payload in state.error) {
                delete state.error[action.payload]
            }
        },
        loadData(state: AdminState, action) {
            const { view, data } = action.payload
            state[view] = data
        },
        clearAdminProject(state: AppState) {
            state.project = null
        },
        updateEditContent(state: AppState, action: PayloadAction<ContentData>) {
            const content = action.payload
            if (state.editContent?.id === content.id || state.editContent == null) {
                state.editContent = { ...content, refresh: Date.now() }
            }
        },
        setEditContent(state: AdminState, action: PayloadAction<ContentData>) {
            state.editContent = action.payload
            if (state.editContent) {
                state.editContent.refresh = Date.now()
            }
            // Convert from hardcoded format
            if (!state.editContent?.id || state.editContent?.id?.length < 36) {
                if ('idx' in state.editContent) {
                    state.editContent.order = state.editContent.idx
                    delete state.editContent.idx
                }

                if (state.editContent?.media && !Array.isArray(state.editContent.media)) {
                    state.editContent.media = [state.editContent.media]
                }
                if (state.editContent?.links && !Array.isArray(state.editContent.links)) {
                    state.editContent.links = [state.editContent.links]
                }
                if (state.editContent?.location && !Array.isArray(state.editContent.location)) {
                    logger.info("Existing location", state.editContent.location)
                    delete state.editContent.location
                }
                if (state.editContent && 'type' in state.editContent) {
                    state.editContent.dynamicContentTypeId = state.editContent.type
                    delete state.editContent.type
                }
            }
            state.editInline = true
            saveState(state)
        },
        clearEditContent(state: AdminState) {
            state.editContent = null
            // state.editInline = false
        },
        setEditInline(state: AdminState, action: PayloadAction<boolean>) {
            state.editInline = action.payload
            saveState(state)
        },
        setProject(state: AdminState, action: PayloadAction<ProjectData>) {
            state.project = action.payload
        },
        setOrganization(state: AdminState, action: PayloadAction<ProjectData>) {
            state.organization = action.payload

            // Also update the organization in config.organizations array to keep it in sync
            if (state.config && state.config.organizations && action.payload?.id) {
                const orgIndex = state.config.organizations.findIndex(org => org.id === action.payload.id)
                if (orgIndex !== -1) {
                    
                    state.config.organizations[orgIndex] = action.payload
                    // Force new reference to trigger updates
                    state.config = { ...state.config, organizations: [...state.config.organizations] }
                }
            }
        },
        setAdminInitialized(state: AdminState, action: PayloadAction<number>) {
            state.initialized = action.payload > 0
        },
        // Template cloning actions
        setTemplateCloning(state: AdminState, action: PayloadAction<{ page: PageData, sourceApp?: AppData, sourceOrganization?: OrganizationData }>) {
            state.templateCloning = {
                ...action.payload,
                timestamp: Date.now()
            }
        },
        clearTemplateCloning(state: AdminState) {
            state.templateCloning = undefined
        },
        /**
         * Optimistically update content order in admin state.
         * Called immediately on reorder so all Redux consumers see new orders instantly.
         */
        optimisticReorder(state: AdminState, action: PayloadAction<{ isApp: boolean, items: { id: string, order: number }[] }>) {
            const { isApp, items } = action.payload
            if (isApp && state.project) {
                state.project = { ...state.project }
                items.forEach(({ id, order }) => {
                    const idx = state.project.dynamicContent.findIndex((x) => x.id === id)
                    if (idx !== -1) {
                        state.project.dynamicContent[idx] = { ...state.project.dynamicContent[idx], order }
                    }
                })
            } else if (!isApp && state.organization) {
                state.organization = { ...state.organization }
                items.forEach(({ id, order }) => {
                    const idx = state.organization.dynamicContent.findIndex((x) => x.id === id)
                    if (idx !== -1) {
                        state.organization.dynamicContent[idx] = { ...state.organization.dynamicContent[idx], order }
                    }
                })
                state.organization.modified = Date.now()
            }
            logger.info(`[Reorder] optimisticReorder ${isApp ? 'app' : 'org'}:`, items.map(x => `${x.id.substring(0, 8)}→${x.order}`).join(', '))
        },
    },
    extraReducers: (builder) => {
        // Prepare admin page for loading
        builder.addCase(initializeAdmin.pending, (state: AdminState, action: { meta: { arg: { appLink: string, organization: OrganizationData } } }) => {
            state.llmContext = null

        })
        // Load main page state
        builder.addCase(
            initializeAdmin.fulfilled,
            (state: AdminState, action: PayloadAction<AdminState>) => {
                state.config = action.payload.config
                if (action.meta.arg != null) {
                    const { appLink, options } = action.meta.arg
                    const { organization, domain } = options

                    document.title = 'Home Gyde Administration'

                    if (domain) {
                        state.customDomain = domain
                    }

                    if (organization && !appLink) {
                        let org = state.config.organizations.find((x) => x.link == ((domain && domain.organization) || organization))
                        if (org) {
                            document.title = `${org.name} Administration`
                        }
                    }

                    if (appLink || domain?.app) {
                        const app = action.payload.apps.find((x) => x.link === ((domain && domain.app) || appLink))
                        if (app) {
                            document.title = `${app.name} Administration`
                        }
                    }
                }

                state.userApps = action.payload.apps
                if (action.payload.organization) {
                    state.organization = action.payload.organization
                } else if (action.meta.arg.organization) {
                    state.organization = action.meta.arg.organization
                }
                state.error = {}

                const media = action.payload.media
                if (state.organization && !action.meta.arg?.options?.editor) {
                    if (state.organization.faviconMediaId && state.organization.faviconMediaId in media) {
                        const faviconMedia = media[state.organization.faviconMediaId]

                        const url = getMediaLink(faviconMedia.link, { organization: state.organization })
                        fnc.changeFavicon(url)
                        return
                    }
                }
                fnc.changeFavicon('/assets/favicon.png')
            },
        )
        // Failed to load main page
        builder.addCase(
            initializeAdmin.rejected,
            (state: AdminState, action: PayloadAction<Error>) => {
                if (action) {
                    logger.error(action.payload)
                }
                state.error.general = ErrorMessage.LoadError
            },
        )
        // Apply retrieved AdminState
        builder.addCase(
            initializeAdminProject.fulfilled,
            (state: AdminState, action: PayloadAction<AdminState>) => {
                // Find in list of apps
                const { success, message, status, media, organization, ...info } = action.payload

                if (organization) {
                    state.organization = organization
                } else if (action.payload.organizations && action.payload.organizations.length > 0) {
                    state.organization = state.config.organizations.find((x) => x.id == action.payload.organizations[0])
                }

                if (!state.organization) {
                    state.organization = state.config.organizations.find((x) => x.link == 'homegyde')
                }

                state.project = setupApp(info, false, state.config)
            },
        )
        builder.addCase(
            initializeAdminProject.rejected,
            (state: AdminState) => {
                state.error = ErrorMessage.LoadError
            },
        )
        // Begin edit, flag in progress
        builder.addCase(updateData.pending, (state: AdminState) => {
            state.editPending = true
            state.error = {}
        })
        // Edit failed
        builder.addCase(updateData.rejected, (state: AdminState, action: PayloadAction<Error>) => {
            logger.error(action.payload)
            state.editPending = false
            if (action.payload && action.payload.view && action.payload.error && action.payload.error.message) {
                const { view, error } = action.payload
                state.error[view] = error.message
            }
        })
        // Edit successful, apply delta to state
        builder.addCase(updateData.fulfilled, (state: AdminState, action: PayloadAction<{ view: AdminView, delta: AdminDelta }>) => {
            state.editPending = false
            const { view, delta } = action.payload
            if (view in state.error) {
                delete state.error
            }

            switch (view) {
                case AdminView.Profile:
                    return

                case AdminView.Availability:
                    for (let i = 0; delta && i < delta.length; i += 1) {
                        try {
                            const unit = state.project.units.find((x) => x.id === delta[i].unitId)
                            unit.availabilityStateId = delta[i].availabilityStateId
                            state.project.maps.unit[unit.id] = unit
                        } catch (e) {
                            logger.error('Error updating units', e)
                        }
                    }
                    break
                case AdminView.SalesTracking:
                    for (let i = 0; delta && i < delta.length; i += 1) {
                        const exists = state.salesTrackings.find((x) => x.id === delta[i].id)
                        if (delta[i]?.status == 'deleted') {
                            if (exists) {
                                // Remove existing sales tracking
                                state.salesTrackings = state.salesTrackings.filter((x) => x.id !== delta[i].id)
                            }
                        } else if (exists) {
                            // Update existing sales tracking
                            Object.assign(exists, delta[i])
                        } else {
                            // Add new sales tracking
                            state.salesTrackings.push(delta[i])
                        }
                    }

                    break
                case AdminView.Pricing:
                    for (let i = 0; delta && i < delta.length; i += 1) {
                        try {

                            // Replace / remove / add override values
                            if (delta[i].unitId != null) {
                                const floorplanIndex = state.project.floorplans
                                    .findIndex((x) => x.id === delta[i].floorplanId)
                                const newFloorplan = { ...state.project.floorplans[floorplanIndex] }
                                state.project.floorplans[floorplanIndex] = newFloorplan
                                const variation = newFloorplan.variations
                                    .find((x) => x.id === delta[i].variationId)
                                const overrideIdx = variation.unitPriceOverrides.findIndex((x) => x.unitId == delta[i].unitId)
                                if (overrideIdx != -1) {
                                    if (!delta[i].price) {
                                        variation.unitPriceOverrides.splice(overrideIdx, 1)
                                    } else {
                                        variation.unitPriceOverrides[overrideIdx].price = delta[i].price
                                    }
                                } else {
                                    variation.unitPriceOverrides.push({ price: delta[i].price, unitId: delta[i].unitId })
                                }
                            } else {
                                // Replace variation price
                                const floorplanIndex = state.project.floorplans
                                    .findIndex((x) => x.id === delta[i].floorplanId)
                                const newFloorplan = { ...state.project.floorplans[floorplanIndex] }
                                state.project.floorplans[floorplanIndex] = newFloorplan
                                const variation = newFloorplan.variations
                                    .find((x) => x.id === delta[i].variationId)
                                variation.price = delta[i].price
                            }
                        } catch (e) {
                            logger.error('Error updating floorplans', e)
                        }
                    }
                    break
                case AdminView.Sitemap: {
                    const { data, failures } = action.payload.delta
                    state.project.sitemaps = data
                    /*const originalId = action.meta.arg.delta.id
                    // Update sitemap
                    const idx = state.project.sitemaps.findIndex((x) => x.id == originalId)
                    if (idx === -1) {
                        state.project.sitemaps.push(delta.data)
                    } else {
                        state.project.sitemaps[idx] = delta
                    }*/
                    break
                }
                case AdminView.Units: {
                    const { unit: units, sites, buildings, buildingTypes } = action.payload.delta
                    state.project = {
                        ...state.project,
                        units,
                        sites,
                        buildings,
                        buildingTypes,
                    }
                    setupAppMaps(state.project)
                    break
                }
                case AdminView.Floorplans: {
                    const { floorplan: floorplans } = action.payload.delta
                    state.project = { ...state.project, floorplans }
                    setupAppMaps(state.project)
                    break
                }
                case AdminView.General: {
                    if (action.payload.delta.pages || action.payload.delta.phases || action.payload.delta.termsAndConditions != null || action.payload.delta.footerDisclaimer != null) {
                        const { pages, phases, termsAndConditions, footerDisclaimer } = action.payload.delta
                        state.project = { ...state.project }
                        if (pages != null) {
                            state.project.pages = setupPages(pages)
                        }
                        if (phases != null) {
                            state.project.phases = phases
                        }
                        if (termsAndConditions != null) {
                            state.project.termsAndConditions = termsAndConditions
                        }
                        if (footerDisclaimer != null) {
                            state.project.meta = { ...state.project.meta, footerDisclaimer }
                        }
                    }
                    if (action.payload.delta.colorSchemes || action.payload.delta.themes) {
                        const { colorSchemes, themes } = action.payload.delta
                        if (colorSchemes) {
                            state.config.colorSchemes = colorSchemes
                        }
                        if (themes) {
                            state.config.themes = themes
                        }
                    }
                    break
                }
                case AdminView.Spin: {
                    if (action.payload.delta.spin) {
                        state.delta.spin.spins = action.payload.delta.spin
                    }
                    break
                }
                case AdminView.Upgrades: {
                    const { failures, messages, status, success, ...upgrades } = action.payload.delta
                    const finalUpgrades = setupUpgrades(upgrades)
                    state.delta.upgrades = { ...state.delta.upgrades, ...finalUpgrades }
                    break
                }
                case AdminView.UpgradesConfig: {
                    const { failures, messages, status, success, ...upgrades } = action.payload.delta
                    const finalUpgrades = setupUpgrades(upgrades)
                    state.delta = { ...state.delta, 'upgrades-config': { ...state.delta['upgrades-config'], ...finalUpgrades } }
                    state.lastSave = Date.now()
                    break
                }
                case AdminView.SEOOptimization: {
                    // SEO data is successfully saved, delta can be cleared
                    state.delta.seooptimization = []
                    state.lastSave = Date.now()
                    break
                }
                default: {
                    if (!delta || delta.id == null) {
                        logger.error(`Invalid ${view}`, delta)
                        return
                    }
                    // Find user and update relevantfields
                    let newData = [...state[view]]
                    const index = newData.findIndex((x) => x.id == delta.id)
                    if (index === -1) {
                        newData.push(delta)
                    } else {
                        newData[index] = {
                            ...newData[index],
                            ...delta,
                        }
                    }
                    state[view] = newData
                    break
                }
            }
        })
        // Begin edit, flag in progress
        builder.addCase(updateProject.pending, (state: AdminState) => {
            state.editPending = true
            state.error = {}
        })
        // Edit failed
        builder.addCase(updateProject.rejected, (state: AdminState, action: PayloadAction<Error>) => {
            logger.error(action.payload)
            state.editPending = false
            if (action.payload && action.payload.view && action.payload.error && action.payload.error.message) {
                const { view, error } = action.payload
                state.error[view] = error.message
            }
        })
        // Edit successful, apply delta to state
        builder.addCase(updateProject.fulfilled, (state: AdminState, action: PayloadAction<{ view: AdminView, delta: AdminDelta }>) => {
            state.editPending = false
            const { view, data } = action.payload
            if (view in state.error) {
                delete state.error
            }

            state.project.meta = data
            setupAppMeta(state.project)
        })

        // Get data
        builder.addCase(retrieveData.fulfilled, (state: AdminState, action) => {
            const { view, data } = action.payload
            let key = view.plural().toCamelCase()
            // if (action.meta.arg.view == 'all-projects') {
            //     key = AdminView.AllProjects
            // } else if (action.meta.arg.view == 'all-organizations') {
            //     key = AdminView.AllOrganizations
            // }
            if (Array.isArray(data)) {
                state[key] = data
            } else if (typeof data == 'object') {
                Object.keys(data).forEach((x) => {
                    state[x] = data[x]
                })
            } else {
                logger.error('Invalid data type')
            }
            return state
        })
        builder.addCase(retrieveData.rejected, (state: AdminState, action) => {
            state.user = { error: ErrorMessage.DataError }
        })
        builder.addCase(removeData.fulfilled, (state: AdminState, action) => {
            const { view, id } = action.payload
            const key = view.plural()
            if (Array.isArray(state[view])) {
                state[key] = removeFromList(id, state[view])
            }
        })
        builder.addCase(removeData.rejected, (state: AdminState, action) => {
            state.error[action.meta.arg.view] = ErrorMessage.DeleteError
        })
        // Get analytics data
        builder.addCase(retrieveAnalytics.pending, (state: AdminState, action) => {
            state.analytics = {}
        })
        builder.addCase(retrieveAnalytics.fulfilled, (state: AdminState, action) => {
            if (action.meta.arg.csv) {
                return
            }
            state.analytics = {}
            const keys = Object.values(Analytics)
            for (let i = 0; i < keys.length; i += 1) {
                if (keys[i] in action.payload) {
                    state.analytics[keys[i]] = action.payload[keys[i]]
                }
            }
        })
        builder.addCase(retrieveAnalytics.rejected, (state: AdminState, action) => {
            state.analytics = { error: ErrorMessage.DataError }
        })
        builder.addCase(upsertPage.fulfilled, (state: AdminState, action: PayloadAction<PageData>) => {
            const pageArr = Array.isArray(action.payload) ? action.payload : [action.payload]
            const pages = action.meta.arg.app ? state.project.pages : state.organization.pages
            const page = action.payload
            const newPages = [...pages]
            pageArr.forEach((x) => {
                const { page } = x
                // Convert ID to type with proper null checking
                if (page.pageTypeId && state.config && state.config.pageTypes) {
                    const configPage = state.config.pageTypes[page.pageTypeId]
                    if (configPage && configPage.name) {
                        page.pageType = configPage.name
                    } else {
                        console.warn('🚨 adminActions - Missing page type config for pageTypeId:', page.pageTypeId)
                        // Fallback to a default or keep the original pageTypeId for debugging
                        page.pageType = `Unknown-${page.pageTypeId}`
                    }
                }
                // delete page.pageTypeId

                const idx = pages.findIndex((x) => {
                    if (x.id == null) {
                        return x.pageType = page.pageType
                    } else {
                        return x.id == page.id
                    }
                })

                if (idx != -1) {
                    newPages[idx] = page
                } else {
                    newPages.push(page)
                }
            })
            if (action.meta.arg.app) {
                state.project.pages = newPages
                action.payload.project = fnc.copyObj(state.project)
            } else {
                state.organization.pages = newPages
                state.organization.modified = Date.now()
                action.payload.organization = fnc.copyObj(state.organization)
            }
        })
        builder.addCase(removePage.fulfilled, (state: AdminState, action: PayloadAction<string>) => {
            const pages = action.meta.arg.app ? state.project.pages : state.organization.pages
            const newPages = [...pages]
            const { page } = action.meta.arg
            const pageIdx = newPages.findIndex((x) => x.id == page.id)
            if (pageIdx !== -1) {
                newPages.splice(pageIdx, 1)
            }
            if (action.meta.arg.app) {
                state.project.pages = newPages
            } else {
                state.organization.pages = newPages
                state.organization.modified = Date.now()
            }
        })
        builder.addCase(addMediaTag.fulfilled, (state: AdminState, action: PayloadAction<MediaTagData>) => {
            const { mediaTag } = action.payload
            let tags = []
            if (action.meta.arg.options.app) {
                tags = state.project.meta.mediaTags
            } else if (action.meta.arg.options.builder) {
                tags = action.meta.arg.options.builder.mediaTags
            } else if (action.meta.arg.options.organization) {
                tags = action.meta.arg.options.organization.mediaTags
            }
            const idx = tags.findIndex((x) => x.id == mediaTag.id)
            let newTags = [...tags]
            if (idx == -1) {
                newTags.push(mediaTag)
            } else {
                newTags[idx] = mediaTag
            }
            if (action.meta.arg.options.app) {
                state.project.meta.mediaTags = newTags
                state.project = { ...state.project }
            } else if (action.meta.arg.options.builder) {
                const idx = state.config.builders.findIndex((x) => x.id == action.meta.arg.options.builder.id)
                if (idx != -1) {
                    state.config.builders[idx].mediaTags = newTags
                }
            } else if (action.meta.arg.options.organization) {
                const idx = state.config.organizations.findIndex((x) => x.id == action.meta.arg.options.organization.id)
                if (idx != -1) {
                    state.config.organizations[idx].mediaTags = newTags
                }
            }
        })

        builder.addCase(removeMediaTag.fulfilled, (state: AdminState, action: PayloadAction<MediaTagData>) => {
            let tags = []
            if (action.meta.arg.options.app) {
                tags = state.project.meta.mediaTags
            } else if (action.meta.arg.options.builder) {
                tags = action.meta.arg.options.builder.mediaTags
            } else if (action.meta.arg.options.organization) {
                tags = action.meta.arg.options.organization.mediaTags
            }
            const idx = tags.findIndex((x) => x.id == action.meta.arg.data.id)
            if (idx != -1) {
                const newTags = [...tags]
                newTags.splice(idx, 1)
                if (action.meta.arg.options.app) {
                    state.project.meta.mediaTags = newTags
                    state.project = { ...state.project }
                } else if (action.meta.arg.options.builder) {
                    const idx = state.config.builders.findIndex((x) => x.id == action.meta.arg.options.builder.id)
                    if (idx != -1) {
                        state.config.builders[idx].mediaTags = newTags
                    }
                } else if (action.meta.arg.options.organization) {
                    const idx = state.config.organizations.findIndex((x) => x.id == action.meta.arg.options.organization.id)
                    if (idx != -1) {
                        state.config.organizations[idx].mediaTags = newTags
                    }
                }
            }
        })
        // Galleries
        builder.addCase(upsertGallery.fulfilled, (state: AdminState, action: PayloadAction<MediaTagData>) => {
            const { gallery } = action.payload
            let galleries = []
            if (action.meta.arg.options.app) {
                galleries = state.project?.galleries
            } else if (action.meta.arg.options.builder) {
                galleries = action.meta.arg.options.builder.galleries
            } else if (action.meta.arg.options.organization) {
                galleries = action.meta.arg.options.organization.galleries
            }
            if (!galleries?.length) {
                return
            }

            const idx = galleries.findIndex((x) => x.id == gallery.id)
            let newGalleries = [...galleries]
            if (idx == -1) {
                newGalleries.push(gallery)
            } else {
                newGalleries[idx] = gallery
            }
            if (action.meta.arg.options.app) {
                state.project.galleries = newGalleries
                state.project = { ...state.project }
            } else if (action.meta.arg.options.builder) {
                const idx = state.config.builders.findIndex((x) => x.id == action.meta.arg.options.builder.id)
                if (idx != -1) {
                    state.config.builders[idx].galleries = newGalleries
                    state.config = { ...state.config }
                }
            } else if (action.meta.arg.options.organization) {
                const idx = state.config.organizations.findIndex((x) => x.id == action.meta.arg.options.organization.id)
                if (idx != -1) {
                    state.config.organizations[idx].galleries = newGalleries
                    state.config = { ...state.config }
                }
                if (action.meta.arg.options.organization.id == state.organization.id) {
                    state.organization = { ...state.organization, galleries: newGalleries, modified: Date.now() }
                }
            }
        })
        builder.addCase(removeGallery.fulfilled, (state: AdminState, action: PayloadAction<MediaTagData>) => {
            let galleries = []
            if (action.meta.arg.options.app) {
                galleries = state.project.galleries
            } else if (action.meta.arg.options.builder) {
                galleries = action.meta.arg.options.builder.galleries
            } else if (action.meta.arg.options.organization) {
                galleries = action.meta.arg.options.organization.galleries
            }
            const idx = galleries.findIndex((x) => x.id == action.meta.arg.data.id)
            if (idx != -1) {
                const newGalleries = [...galleries]
                newGalleries.splice(idx, 1)
                if (action.meta.arg.options.app) {
                    state.project.galleries = newGalleries
                    state.project = { ...state.project }
                } else if (action.meta.arg.options.builder) {
                    const idx = state.config.builders.findIndex((x) => x.id == action.meta.arg.options.builder.id)
                    if (idx != -1) {
                        state.config.builders[idx].galleries = newGalleries
                        state.config = { ...state.config }
                    }
                } else {
                    const idx = state.config.organizations.findIndex((x) => x.id == action.meta.arg.options.organization.id)
                    if (idx != -1) {
                        state.config.organizations[idx].galleries = newGalleries
                        state.config = { ...state.config }
                    }
                    if (action.meta.arg.options.organization.id == state.organization.id) {
                        state.organization = { ...state.organization, galleries: newGalleries, modified: Date.now() }
                    }
                }
            }
        })
        builder.addCase(upsertContent.fulfilled, (state: AdminState, action: PayloadAction<ContentData>) => {
            logger.info(`[Reorder] upsertContent.fulfilled id=${action.payload?.id?.substring(0, 8)} order=${action.payload?.order} ${action.meta.arg.app ? 'app' : 'org'}`)
            if (action.meta.arg.app) {
                if (!state.project) {
                    logger.warn('[Reorder] upsertContent.fulfilled - No project in state')
                    return
                }
                const idx = state.project?.dynamicContent.findIndex((x) => x.id == action.payload.id)
                logger.info(`[Reorder] upsert project idx=${idx} oldOrder=${idx > -1 ? state.project.dynamicContent[idx]?.order : 'N/A'} newOrder=${action.payload?.order}`)
                state.project = { ...state.project }
                // state.editContent = action.payload
                if (idx == -1) {
                    state.project.dynamicContent.push(action.payload)
                } else {
                    state.project.dynamicContent[idx] = action.payload
                }
            } else if (action.meta.arg.organization) {
                const idx = state.organization?.dynamicContent.findIndex((x) => x.id == action.payload.id)
                logger.info(`[Reorder] upsert org idx=${idx} oldOrder=${idx > -1 ? state.organization.dynamicContent[idx]?.order : 'N/A'} newOrder=${action.payload?.order}`)
                state.organization = { ...state.organization }
                // state.editContent = action.payload
                if (idx == -1) {
                    state.organization.dynamicContent.push(action.payload)
                } else {
                    state.organization.dynamicContent[idx] = action.payload
                }
                state.organization.modified = Date.now()
            }
            // setEditContent(action)
        })
        builder.addCase(batchUpsertContent.fulfilled, (state: AdminState, action) => {
            const items = action.payload as any[]
            const hasApp = !!action.meta.arg.app
            const hasOrg = !!action.meta.arg.organization
            logger.info('[Reorder] batchUpsertContent.fulfilled - Applying', items.length, 'items atomically')
            if (hasApp) {
                if (!state.project) return
                state.project = { ...state.project }
                items.forEach((item) => {
                    const idx = state.project.dynamicContent.findIndex((x) => x.id == item.id)
                    if (idx == -1) {
                        state.project.dynamicContent.push(item)
                    } else {
                        state.project.dynamicContent[idx] = item
                    }
                })
            } else if (hasOrg) {
                state.organization = { ...state.organization }
                items.forEach((item) => {
                    const idx = state.organization.dynamicContent.findIndex((x) => x.id == item.id)
                    if (idx == -1) {
                        state.organization.dynamicContent.push(item)
                    } else {
                        state.organization.dynamicContent[idx] = item
                    }
                })
                state.organization.modified = Date.now()
            }
        })
        builder.addCase(removeContent.fulfilled, (state: AdminState, action: PayloadAction<ContentData>) => {
            if (action.meta.arg.app) {
                if (!state.project) return
                state.project = { ...state.project }
                const idx = state.project?.dynamicContent.findIndex((x) => x.id == action.meta.arg.data.id)
                if (idx != -1) {
                    state.project.dynamicContent.splice(idx, 1)
                }
            } else if (action.meta.arg.organization) {
                if (!state.organization) return
                state.organization = { ...state.organization }
                const idx = state.organization?.dynamicContent.findIndex((x) => x.id == action.meta.arg.data.id)
                if (idx != -1) {
                    state.organization.dynamicContent.splice(idx, 1)
                }
                state.organization.modified = Date.now()
            }
        })
        builder.addCase(createLocation.fulfilled, (state: AdminState, action: PayloadAction<LocationData>) => {
            // See if 
            let source = null
            if (action.meta.arg.app) {
                if (state.apps) {
                    let sourceIdx = state.apps.findIndex((x) => x.id == action.meta.arg.app.meta?.id)
                    const newApps = [...state.apps]
                    if (sourceIdx != -1) {
                        source = newApps[sourceIdx]
                        state.delta = { ...state.delta, apps: newApps }
                    }
                } else if (state.project && state.project.id == action.meta.arg.app.id) {
                    source = state.project
                }
            } else if (action.meta.arg.organization) {
                source = state.organizations.find((x) => x.id == action.meta.arg.organization.id)
                state.delta = { ...state.delta, organizations: source }
            }
            if (source) {
                source.locations = [...source.locations || []]
                const idx = source.locations.findIndex((x) => x.id == action.payload.id)
                if (idx != -1) {
                    source.locations[idx] = action.payload
                } else {
                    source.locations.push(action.payload)
                }
            }
        })
        builder.addCase(removeLocation.fulfilled, (state: AdminState, action: PayloadAction<LocationData>) => {
            let source = null
            if (action.meta.arg.app) {
                if (state.apps) {
                    let sourceIdx = state.apps.findIndex((x) => x.id == action.meta.arg.app.meta?.id)
                    const newApps = [...state.apps]
                    if (sourceIdx != -1) {
                        source = newApps[sourceIdx]
                        state.delta = { ...state.delta, apps: newApps }
                    }
                } else if (state.project && state.project.id == action.meta.arg.app.meta.id) {
                    source = state.project
                }
            } else if (action.meta.arg.organization) {
                source = state.organizations.find((x) => x.id == action.meta.arg.organization.id)
                state.delta = { ...state.delta, organizations: source }
            }
            if (source) {
                source.locations = [...source.locations]
                const idx = source.locations.findIndex((x) => x.id == action.meta.arg.data.id)
                if (idx != -1) {
                    source.locations.splice(idx, 1)
                }
            }
        })
        builder.addCase(retrieveLLMContext.fulfilled, (state: AdminState, action: PayloadAction<LLMContextData>) => {
            state.llmContext = action.payload.data
        })
        builder.addCase(createLLMContext.fulfilled, (state: AdminState, action: PayloadAction<LLMContextData>) => {
            const index = state.llmContext.findIndex((x) => x.id === action.payload.data.id);
            if (index === -1) {
                state.llmContext.push(action.payload.data)
            } else {
                state.llmContext[index] = action.payload.data
            }
        })
        builder.addCase(removeLLMContext.fulfilled, (state: AdminState, action: PayloadAction<LLMContextData>) => {
            const index = state.llmContext.findIndex((x) => x.id === action.meta.arg.data.id);
            if (index !== -1) {
                state.llmContext.splice(index, 1);
            }
        })

        // Documentation link reducers
        builder.addCase(fetchDocumentationLinks.fulfilled, (state: AdminState, action) => {
            if (action.payload && action.payload.data) {
                state.documentationLinks = action.payload.data
            }
        })

        builder.addCase(fetchDocumentationLinkById.fulfilled, (state: AdminState, action) => {
            if (action.payload && action.payload.link) {
                const index = state.documentationLinks.findIndex(link => link.id === action.payload.link.id)
                if (index !== -1) {
                    state.documentationLinks[index] = action.payload.link
                } else {
                    state.documentationLinks.push(action.payload.link)
                }
            }
        })


        builder.addCase(upsertDocumentationLink.fulfilled, (state: AdminState, action) => {
            if (action.payload && action.payload.data) {
                const index = state.documentationLinks.findIndex(link => link.id === action.payload.data.id)
                if (index !== -1) {
                    state.documentationLinks[index] = action.payload.data
                } else {
                    state.documentationLinks.push(action.payload.data)
                }
            }
        })

        builder.addCase(removeDocumentationLink.fulfilled, (state: AdminState, action) => {
            if (action.payload && action.payload.id) {
                const index = state.documentationLinks.findIndex(link => link.id === action.payload.id)
                if (index !== -1) {
                    state.documentationLinks.splice(index, 1)
                }
            }
        })

        // Marketing spend reducers
        builder.addCase(retrieveMarketingSpend.fulfilled, (state: AdminState, action) => {
            if (action.payload && action.payload['marketing-spend']) {
                state.marketingSpend = action.payload['marketing-spend']
            }
            if (action.payload && action.payload.channels) {
                state.marketingSpendChannels = action.payload.channels
            }
        })
        builder.addCase(retrieveMarketingSpend.rejected, (state: AdminState, action) => {
            state.marketingSpend = []
        })
        builder.addCase(upsertMarketingSpend.fulfilled, (state: AdminState, action) => {
            if (action.payload && action.payload.data) {
                state.marketingSpend = action.payload.data
            }
        })
        builder.addCase(importMarketingSpendCsv.fulfilled, (state: AdminState, action) => {
            if (action.payload && action.payload.data) {
                // Merge imported data into existing marketing spend
                const imported = action.payload.data
                if (Array.isArray(imported)) {
                    state.marketingSpend = [...state.marketingSpend, ...imported]
                    // Update channels list from the merged data
                    const allChannels = Array.from(
                        new Set(state.marketingSpend.map((d: any) => d.channel || 'total'))
                    ) as string[]
                    state.marketingSpendChannels = allChannels
                }
            }
        })

        // Sales tracking reducers
        builder.addCase(retrieveSalesTracking.fulfilled, (state: AdminState, action) => {
            if (action.payload && action.payload['sales_tracking']) {
                state.salesTrackings = action.payload['sales_tracking']
            }
        })
        builder.addCase(retrieveSalesTracking.rejected, (state: AdminState, action) => {
            state.salesTrackings = []
        })
        builder.addCase(createSalesTrackingBulk.fulfilled, (state: AdminState, action) => {
            if (action.payload && action.payload.data) {
                state.salesTrackings = action.payload.data
            }
        })

        // Handle generic template cloning completion in admin context
        builder.addCase(cloneTemplateToContext.fulfilled, (state: AdminState, action: PayloadAction<{ page: PageData, app?: AppData, organization?: OrganizationData, dynamicContent?: ContentData[], createdGalleries?: any[] }>) => {
            let newPage = action.payload.page
            const newDynamicContent = action.payload.dynamicContent || action.payload.dynamic_content
            const updatedProject = action.payload.app
            const updatedOrganization = action.payload.organization
            const createdGalleries = action.payload.createdGalleries || action.payload.created_galleries || []
            const targetContext = action.meta.arg.targetContext

            // Extract and collect all media from the response
            const allNewMedia = new Map()

            // 1. Media from updated project/organization
            if (updatedProject && updatedProject.media) {
                
                Object.entries(updatedProject.media).forEach(([mediaId, mediaData]) => {
                    allNewMedia.set(mediaId, mediaData)
                    
                })
            }

            if (updatedOrganization && updatedOrganization.media) {
                
                Object.entries(updatedOrganization.media).forEach(([mediaId, mediaData]) => {
                    allNewMedia.set(mediaId, mediaData)
                    
                })
            }

            // 2. Media from created galleries
            if (createdGalleries && Array.isArray(createdGalleries)) {
                
                createdGalleries.forEach(gallery => {
                    

                    if (gallery.media && Array.isArray(gallery.media)) {
                        gallery.media.forEach(mediaItem => {
                            // Handle different media item formats
                            let mediaId = null
                            let mediaData = null

                            if (typeof mediaItem === 'string') {
                                mediaId = mediaItem
                                // Try to find media data in other sources
                                if (updatedProject?.media?.[mediaId]) {
                                    mediaData = updatedProject.media[mediaId]
                                } else if (updatedOrganization?.media?.[mediaId]) {
                                    mediaData = updatedOrganization.media[mediaId]
                                }
                            } else if (mediaItem && typeof mediaItem === 'object') {
                                // Extract media ID from different possible structures
                                mediaId = mediaItem.id || mediaItem.mediaId || mediaItem.media_id

                                if (mediaItem.id && (mediaItem.name || mediaItem.link)) {
                                    // This looks like full media data
                                    mediaData = mediaItem
                                } else if (mediaItem.mediaId || mediaItem.media_id) {
                                    // This might be a gallery media relationship object
                                    const actualMediaId = mediaItem.mediaId || mediaItem.media_id
                                    if (updatedProject?.media?.[actualMediaId]) {
                                        mediaData = updatedProject.media[actualMediaId]
                                        mediaId = actualMediaId
                                    } else if (updatedOrganization?.media?.[actualMediaId]) {
                                        mediaData = updatedOrganization.media[actualMediaId]
                                        mediaId = actualMediaId
                                    }
                                }
                            }

                            if (mediaId && mediaData) {
                                allNewMedia.set(mediaId, mediaData)
                            } else if (mediaId) {
                                console.warn('🚨 AdminActions Found media ID but no media data:', {
                                    mediaId,
                                    mediaItem,
                                    galleryId: gallery.id,
                                    galleryName: gallery.name
                                })
                            }
                        })
                    }
                })
            }

            // 3. Media referenced in dynamic content
            if (newDynamicContent && Array.isArray(newDynamicContent)) {
                
                newDynamicContent.forEach(content => {
                    

                    // Check for media in the content
                    if (content.media && Array.isArray(content.media)) {
                        content.media.forEach(mediaRef => {
                            const mediaId = mediaRef.mediaId || mediaRef.id
                            if (mediaId) {
                                // Try to find media data
                                let mediaData = null
                                if (updatedProject?.media?.[mediaId]) {
                                    mediaData = updatedProject.media[mediaId]
                                } else if (updatedOrganization?.media?.[mediaId]) {
                                    mediaData = updatedOrganization.media[mediaId]
                                }

                                if (mediaData) {
                                    allNewMedia.set(mediaId, mediaData)
                                }
                            }
                        })
                    }
                })
            }

            // 4. Add all collected media to Redux state
            if (allNewMedia.size > 0) {
                
                const mediaObject = Object.fromEntries(allNewMedia)
                addMedia(state, mediaObject)
            }

            // Safe pageType mapping with null checks
            if (newPage && newPage.pageTypeId && state.config && state.config.pageTypes) {
                const pageTypeConfig = state.config.pageTypes[newPage.pageTypeId]
                newPage = {
                    ...newPage,
                    pageType: pageTypeConfig?.name || `Unknown-${newPage.pageTypeId}`
                }
            } else if (newPage && !newPage.pageType) {
                newPage = { ...newPage, pageType: PageType.Custom }
            }

            // Handle app context updates
            if (targetContext.type === 'app') {
                const currentProjectId = state.project?.meta?.id || state.project?.id
                if (state.project && currentProjectId === targetContext.id) {
                    // If we have updated project data from API, use it as base
                    if (updatedProject && (updatedProject.pages || updatedProject.meta)) {
                        
                        // Merge the updated project data into current project
                        if (updatedProject.meta) {
                            state.project.meta = { ...state.project.meta, ...updatedProject.meta }
                        }
                        if (updatedProject.pages) {
                            state.project.pages = [...updatedProject.pages]
                        }
                        if (updatedProject.dynamicContent) {
                            state.project.dynamicContent = [...updatedProject.dynamicContent]
                        }
                        if (updatedProject.galleries) {
                            state.project.galleries = [...updatedProject.galleries]
                        }
                    }

                    // Ensure the new page is added if not already in the pages array
                    if (newPage) {
                        const pageExists = state.project.pages.find(page => page.id === newPage.id)
                        if (!pageExists) {
                            
                            state.project.pages = [...state.project.pages, newPage]
                        }
                    }

                    // Add new dynamic content if it exists
                    if (newDynamicContent && Array.isArray(newDynamicContent) && newDynamicContent.length > 0) {
                        const currentDynamicContent = state.project.dynamicContent || []
                        const newContent = newDynamicContent.filter(nc => !currentDynamicContent.some(ec => ec.id === nc.id))
                        if (newContent.length > 0) {
                            state.project.dynamicContent = [...currentDynamicContent, ...newContent]
                        }
                    }

                    // Add created galleries to the admin project state
                    if (createdGalleries && Array.isArray(createdGalleries) && createdGalleries.length > 0) {
                        const currentGalleries = state.project.galleries || []
                        const oldGalleriesCount = currentGalleries.length
                        // Filter out any duplicate galleries before adding
                        const newGalleries = createdGalleries.filter(ng => !currentGalleries.some(cg => cg.id === ng.id))
                        if (newGalleries.length > 0) {
                            state.project.galleries = [...currentGalleries, ...newGalleries]
                        }
                    }

                    // Force state update
                    state.project = { ...state.project }
                }
            }

            // Handle organization context updates
            if (targetContext.type === 'organization' && newPage && state.organization && state.organization.id === targetContext.id) {
                // Add new page to organization
                if (newPage) {
                    const pageIndex = state.organization.pages.findIndex(page => page.id === newPage.id)
                    if (pageIndex === -1) {
                        state.organization.pages = [...state.organization.pages, newPage]
                    } else {
                        state.organization.pages[pageIndex] = newPage
                    }
                    state.organization.modified = Date.now()
                    state.organization = { ...state.organization }
                }

                // Add new dynamic content to organization
                if (newDynamicContent && newDynamicContent.length) {
                    const currentDynamicContent = state.organization.dynamicContent || []
                    const newContent = newDynamicContent.filter(nc => !currentDynamicContent.some(ec => ec.id === nc.id))
                    if (newContent.length > 0) {
                        state.organization.dynamicContent = [...currentDynamicContent, ...newContent]
                    }
                }

                // Add created galleries to organization
                if (createdGalleries && Array.isArray(createdGalleries) && createdGalleries.length > 0) {
                    const currentGalleries = state.organization.galleries || []
                    const newGalleries = createdGalleries.filter(ng => !currentGalleries.some(cg => cg.id === ng.id))
                    if (newGalleries.length > 0) {
                        state.organization.galleries = [...currentGalleries, ...newGalleries]
                        state.organization.modified = Date.now()
                    }
                }
            }
        })

        builder.addCase(exportMediaFile.fulfilled, (state: AdminState, action: PayloadAction<Dict>) => {
            // Export doesn't need state updates
        })

    }
})

export const {
    setChangePending,
    setDelta,
    resetDelta,
    resetAdminError,
    loadData,
    clearAdminProject,
    setEditContent,
    clearEditContent,
    updateEditContent,
    setEditInline,
    setProject,
    setOrganization,
    setAdminInitialized,
    setTemplateCloning,
    clearTemplateCloning,
    optimisticReorder
} = adminSlice.actions

export default adminSlice.reducer
